/**
 * Уровень территории по коду КАТО (US-11 FR-1.1).
 *
 * Код читается по обнулённым разрядам `РР ДД СС ННН` — та же семантика,
 * что на бэкенде в управлении видимостью виджетов (US-09 §1), поэтому и
 * названия уровней совпадают:
 *
 *   270000000 → OBLAST     область
 *   273200000 → DISTRICT   район / город областного значения
 *   273233000 → OKRUG      сельский округ
 *   273233100 → VILLAGE    село
 *
 * Отдельного уровня «город» нет: города областного значения лежат в
 * справочнике на том же уровне, что районы, и по одному коду КАТО от
 * района не отличаются — признак города должен прийти из данных
 * (US-12 FR-5, сценарий 11).
 */
export type KatoLevel = 'OBLAST' | 'DISTRICT' | 'OKRUG' | 'VILLAGE';

/** null — код не похож на КАТО (не 9 разрядов / не число). */
export const katoLevel = (kato: number): KatoLevel | null => {
  if (!Number.isInteger(kato) || kato <= 0 || kato > 999_999_999) return null;
  const village = kato % 1_000;
  const okrug = Math.trunc(kato / 1_000) % 100;
  const district = Math.trunc(kato / 100_000) % 100;
  if (village !== 0) return 'VILLAGE';
  if (okrug !== 0) return 'OKRUG';
  if (district !== 0) return 'DISTRICT';
  return 'OBLAST';
};

/**
 * Районный состав вкладки «Цифровой паспорт» (US-11): применяется на
 * уровне района и города областного значения, на прочих уровнях состав
 * вкладки не меняется (FR-1.1).
 */
export const isDistrictPassportLevel = (kato: number): boolean =>
  katoLevel(kato) === 'DISTRICT';

/**
 * Сельский округ или село — уровни, на которых часть показателей
 * «Аналитики» не показывается вовсе (DP-522).
 */
export const isRuralLevel = (kato: number | null): boolean => {
  if (kato === null) return false;
  const level = katoLevel(kato);
  return level === 'OKRUG' || level === 'VILLAGE';
};
